/**
 * SectionBreadcrumb — Shows the hierarchical path for a nested section
 * in the config panel. Clickable ancestor segments for quick navigation.
 *
 * Returns null for root-level sections (depth 0).
 */

import { findSectionByEditorId, WIDGET_REGISTRY } from '@zaviago/storefront-config'
import type { EditorSection } from './usePageSections'

interface SectionBreadcrumbProps {
  sections: EditorSection[]
  editorId: string
  onSelect: (editorId: string) => void
}

/** Humanize a type key like "product-grid" → "Product Grid" */
function getLabel(section: EditorSection): string {
  const widget = WIDGET_REGISTRY[section.type]
  return (
    widget?.schema.label ??
    section.type.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  )
}

export function SectionBreadcrumb({ sections, editorId, onSelect }: SectionBreadcrumbProps) {
  const lookup = findSectionByEditorId(sections, editorId)
  if (!lookup || lookup.depth === 0) return null

  // Walk the path array from root to build the full ancestor chain
  const chain: { editorId: string; label: string }[] = []
  let cursor: EditorSection[] = sections
  for (let i = 0; i < lookup.path.length; i++) {
    const node = cursor[lookup.path[i]]
    if (!node) break
    chain.push({ editorId: node._editorId, label: getLabel(node) })
    cursor = (node.children ?? []) as EditorSection[]
  }

  if (chain.length <= 1) return null

  return (
    <nav className="mb-3 flex flex-wrap items-center gap-1 text-sm" aria-label="Section path">
      {chain.map((seg, i) => {
        const isLast = i === chain.length - 1
        return (
          <span key={seg.editorId} className="flex items-center gap-1">
            {i > 0 && <span className="text-gray-300">›</span>}
            {isLast ? (
              <span className="font-semibold text-gray-900">{seg.label}</span>
            ) : (
              <button
                className="cursor-pointer text-sky-600 hover:underline"
                onClick={() => onSelect(seg.editorId)}
              >
                {seg.label}
              </button>
            )}
          </span>
        )
      })}
    </nav>
  )
}
